// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation). Pauses on hover, keyboard focus, and touch
// (touch resumes a few seconds after the finger lifts). Cloned items are hidden from assistive
// tech and removed from the tab order so each link is announced and focused once.
// One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hovered = false;
  #focused = false;
  #touched = false;
  #touchTimer = 0;

  // Stay still in the theme editor and in framed previews (Canvas, admin preview) so merchants
  // can click and select the original items instead of moving clones.
  static #isEditing(el) {
    if (el.hasAttribute("data-static") || window.Shopify?.designMode) return true;
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), opts);

    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, opts);
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, opts);
    this.addEventListener("focusin", () => { this.#focused = true; this.#sync(); }, opts);
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget)) { this.#focused = false; this.#sync(); }
    }, opts);
    this.addEventListener("touchstart", () => {
      clearTimeout(this.#touchTimer);
      this.#touched = true;
      this.#sync();
    }, { ...opts, passive: true });
    this.addEventListener("touchend", () => {
      clearTimeout(this.#touchTimer);
      this.#touchTimer = setTimeout(() => { this.#touched = false; this.#sync(); }, 3000);
    }, { ...opts, passive: true });

    this.#setup();
    // Web fonts can change the measured width; re-measure once they settle.
    document.fonts?.ready.then(() => { if (this.isConnected) this.#setup(); });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#touchTimer);
    this.#animation?.cancel();
  }

  #sync() {
    if (!this.#animation) return;
    if (this.#hovered || this.#focused || this.#touched) {
      this.#animation.pause();
    } else {
      this.#animation.play();
    }
  }

  #setup() {
    const progress = this.#animation?.currentTime ?? 0;
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    this.removeAttribute("data-animating");
    if (!track || this.#mql.matches || SMarquee.#isEditing(this)) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        if (clone.matches("a, button")) clone.setAttribute("tabindex", "-1");
        clone.querySelectorAll("a, button").forEach((el) => el.setAttribute("tabindex", "-1"));
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#animation.currentTime = progress;
    this.setAttribute("data-animating", "");
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
